import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

// Agendar reunião a partir de uma conversa do Chat CRM.
// Fluxo: escolher o usuário (dono da agenda) → escolher o dia/horário na agenda dele
// → detalhes da reunião + lembrete ao contato → confirmação.
//
// Lembrete: WhatsApp não oficial = texto livre; WhatsApp oficial = template aprovado;
// outros canais (Instagram, Facebook, Shopee...) = sem lembrete, só a reunião.

type SchedulableUser = { id: string; nome: string; email: string; isMe?: boolean };
type AgendaItem = { kind: 'event' | 'meeting' | 'task'; id: string; title: string; at: string; endAt?: string | null; status?: string; contactName?: string | null };
type NotificationCapability = { mode: 'text' | 'template' | 'none'; reason: string };
type ReminderOffset = { minutes: number; label: string };
type ReminderVariable = { key: string; label: string };
type TemplatePosition = { key: string; section: string; slot: string; label: string };
type TemplateComponent = { type?: string; format?: string; text?: string; buttons?: { text?: string }[] };
type OfficialTemplate = { name: string; language: string; category?: string | null; components?: TemplateComponent[]; variableOrder?: string[]; positions: TemplatePosition[]; headerFormat?: string | null };
type ConversationContext = {
  conversation: { id: string; provider: string; platform: string; channelType: string; contactId?: string | null; contactName: string; contactPhone?: string | null };
  notification: NotificationCapability;
  defaults: { title: string; reminderMessage: string; durationMinutes: number; reminderMinutes: number };
  reminderVariables: ReminderVariable[];
  reminderOffsets: ReminderOffset[];
  meetings: ExistingMeeting[];
};
export type ExistingMeeting = {
  id: string; title: string; status: string; startAt: string; endAt?: string | null; meetingMode?: string | null; location?: string | null;
  user?: { id: string; nome: string } | null; createdBy?: { id: string; nome: string } | null;
  reminders?: { id: string; status: string; scheduledFor: string; kind: string; error?: string | null }[];
};

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DURATIONS = [15, 30, 45, 60, 90, 120];
const MODES: { value: string; label: string; hint: string }[] = [
  { value: 'ONLINE', label: 'Online', hint: 'Link da chamada (Meet, Zoom, Teams...)' },
  { value: 'PRESENCIAL', label: 'Presencial', hint: 'Endereço do encontro' },
  { value: 'TELEFONE', label: 'Telefone', hint: 'Número que será usado' }
];
export const MEETING_STATUS_LABELS: Record<string, string> = { SCHEDULED: 'Agendada', CONFIRMED: 'Confirmada', DONE: 'Realizada', CANCELLED: 'Cancelada', NO_SHOW: 'Não compareceu' };
export const MEETING_STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-amber-100 text-amber-800', CONFIRMED: 'bg-emerald-100 text-emerald-800', DONE: 'bg-slate-200 text-slate-700', CANCELLED: 'bg-red-100 text-red-700', NO_SHOW: 'bg-rose-100 text-rose-700'
};
const TEMPLATE_TEXT_PREFIX = 'text:';

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
export async function meetingFetch<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...authHeaders(), ...((options.headers as Record<string, string>) || {}) };
  const response = await fetch(url, { ...options, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error: any = new Error(data.message || `Erro ${response.status}`);
    error.code = data.code; error.details = data.details; error.status = response.status;
    throw error;
  }
  return data;
}

// ---------- datas ----------
const pad = (n: number) => String(n).padStart(2, '0');
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const timeStr = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const dateStr = (d: Date) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const weekdayStr = (d: Date) => d.toLocaleDateString('pt-BR', { weekday: 'long' });
function monthGrid(base: Date): Date[] {
  const first = new Date(base.getFullYear(), base.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
}
const hasTime = (d: Date) => d.getHours() !== 0 || d.getMinutes() !== 0;

function chipClass(item: AgendaItem) {
  if (item.kind === 'meeting') return item.status === 'CANCELLED' ? 'bg-slate-100 text-slate-400 line-through' : 'bg-amber-100 text-amber-800';
  if (item.kind === 'event') return 'bg-violet-100 text-violet-800';
  return 'bg-slate-100 text-slate-600';
}

function offsetLabel(minutes: number, offsets: ReminderOffset[]) {
  const known = offsets.find(o => o.minutes === minutes);
  if (known) return known.label;
  if (minutes % 1440 === 0) return `${minutes / 1440} dia(s) antes`;
  if (minutes % 60 === 0) return `${minutes / 60} hora(s) antes`;
  return `${minutes} minuto(s) antes`;
}

// Mesmas variáveis do backend (meetingService.buildReminderVars) para a prévia.
function buildVars(input: { contactName: string; title: string; startAt: Date; responsavel: string; mode: string; location: string; description: string }) {
  const contactName = input.contactName.trim();
  const location = input.location.trim();
  let localLine = '';
  if (location) {
    if (input.mode === 'ONLINE') localLine = `Link da reunião: ${location}`;
    else if (input.mode === 'TELEFONE') localLine = `Vamos falar por telefone: ${location}`;
    else localLine = `Local: ${location}`;
  } else if (input.mode === 'TELEFONE') localLine = 'Vamos falar por telefone.';
  return {
    nome: contactName, primeiroNome: contactName.split(/\s+/)[0] || '', titulo: input.title,
    data: dateStr(input.startAt), hora: timeStr(input.startAt), diaSemana: weekdayStr(input.startAt),
    responsavel: input.responsavel, modo: MODES.find(m => m.value === input.mode)?.label || '', local: localLine, descricao: input.description.trim()
  } as Record<string, string>;
}
const renderVars = (template: string, vars: Record<string, string>) =>
  template.replace(/\{\{\s*([a-zA-Z_]+)\s*\}\}/g, (_m, key) => vars[key] ?? '').replace(/\n{3,}/g, '\n\n').trim();

function renderTemplatePreview(template: OfficialTemplate, params: string[]) {
  const list = template.components || [];
  const valueOf = (key: string) => { const i = template.positions.findIndex(p => p.key === key); return i >= 0 ? (params[i] || '') : ''; };
  const fill = (text: string | undefined, section: string) => String(text || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (_m, slot) => valueOf(`${section}:${slot}`));
  const typeOf = (c: TemplateComponent) => String(c?.type || '').toLowerCase();
  const blocks: string[] = [];
  const header = list.find(c => typeOf(c) === 'header');
  if (header && String(header.format || 'text').toLowerCase() === 'text') { const t = fill(header.text, 'header').trim(); if (t) blocks.push(t); }
  const body = list.find(c => typeOf(c) === 'body');
  if (body) { const t = fill(body.text, 'body').trim(); if (t) blocks.push(t); }
  const footer = list.find(c => typeOf(c) === 'footer');
  if (footer && String(footer.text || '').trim()) blocks.push(String(footer.text).trim());
  const buttons = list.filter(c => typeOf(c) === 'buttons').flatMap(c => c.buttons || []).map(b => String(b?.text || '').trim()).filter(Boolean);
  if (buttons.length) blocks.push(buttons.map(l => `[ ${l} ]`).join('  '));
  return blocks.join('\n\n');
}

type Step = 'user' | 'calendar' | 'form' | 'done';

export function ScheduleMeetingModal({ conversationId, onClose, onScheduled }: {
  conversationId: string;
  onClose: () => void;
  onScheduled?: (meeting: ExistingMeeting) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<SchedulableUser[]>([]);
  const [context, setContext] = useState<ConversationContext | null>(null);
  const [step, setStep] = useState<Step>('user');
  const [targetUser, setTargetUser] = useState<SchedulableUser | null>(null);

  // agenda
  const [base, setBase] = useState(new Date());
  const [agenda, setAgenda] = useState<AgendaItem[]>([]);
  const [agendaLoading, setAgendaLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  // formulário
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('09:00');
  const [duration, setDuration] = useState(60);
  const [mode, setMode] = useState('ONLINE');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [notify, setNotify] = useState(true);
  const [minutesBefore, setMinutesBefore] = useState(60);
  const [customMinutes, setCustomMinutes] = useState('');
  const [message, setMessage] = useState('');
  const [templates, setTemplates] = useState<OfficialTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templateParams, setTemplateParams] = useState<string[]>([]);
  const [templateHeaderMedia, setTemplateHeaderMedia] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<{ id: string; title: string; startAt: string; endAt?: string | null }[] | null>(null);
  const [result, setResult] = useState<{ event: ExistingMeeting; reminder: any; reminderWarning: string | null } | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // -------- carga inicial
  useEffect(() => {
    (async () => {
      try {
        const [perm, ctx] = await Promise.all([
          meetingFetch(`/api/meetings/permissions/me`),
          meetingFetch<ConversationContext>(`/api/meetings/conversation/${conversationId}/context`)
        ]);
        const list: SchedulableUser[] = perm.users || [];
        setUsers(list);
        setContext(ctx);
        setTitle(ctx.defaults.title);
        setDuration(ctx.defaults.durationMinutes || 60);
        setMinutesBefore(ctx.defaults.reminderMinutes || 60);
        setMessage(ctx.defaults.reminderMessage);
        setNotify(ctx.notification.mode !== 'none');
        if (list.length === 0) {
          toast.error(perm.permissions?.canSchedule === false
            ? 'Você não tem permissão para agendar reuniões. Peça ao administrador em Business Core > Usuários > Permissões.'
            : 'Nenhum usuário disponível para agendar.');
          onClose();
          return;
        }
        if (list.length === 1) { setTargetUser(list[0]); setStep('calendar'); }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Erro ao abrir agendamento');
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [conversationId]);

  // -------- agenda do usuário-alvo no mês exibido
  useEffect(() => {
    if (!targetUser || step !== 'calendar') return;
    const cells = monthGrid(base);
    const start = cells[0];
    const end = new Date(cells[cells.length - 1]); end.setHours(23, 59, 59, 999);
    setAgendaLoading(true);
    meetingFetch(`/api/meetings/agenda?userId=${targetUser.id}&start=${start.toISOString()}&end=${end.toISOString()}`)
      .then(data => setAgenda(data.items || []))
      .catch(err => toast.error(err instanceof Error ? err.message : 'Erro ao carregar agenda'))
      .finally(() => setAgendaLoading(false));
  }, [targetUser, base, step]);

  const itemsByDay = useMemo(() => {
    const map: Record<string, AgendaItem[]> = {};
    agenda.forEach(item => { const k = dayKey(new Date(item.at)); (map[k] = map[k] || []).push(item); });
    Object.values(map).forEach(arr => arr.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()));
    return map;
  }, [agenda]);

  // -------- templates (WhatsApp oficial)
  const notificationMode = context?.notification.mode || 'none';
  useEffect(() => {
    if (step !== 'form' || notificationMode !== 'template' || templates.length || templatesLoading) return;
    setTemplatesLoading(true);
    setTemplatesError(null);
    meetingFetch(`/api/crm-chat/conversations/${conversationId}/templates`)
      .then(data => setTemplates(Array.isArray(data.data) ? data.data : []))
      .catch(err => setTemplatesError(err instanceof Error ? err.message : 'Erro ao carregar templates'))
      .finally(() => setTemplatesLoading(false));
  }, [step, notificationMode, conversationId]);

  const startAt = useMemo(() => {
    if (!selectedDay) return null;
    const [h, m] = time.split(':').map(Number);
    const d = new Date(selectedDay); d.setHours(h || 0, m || 0, 0, 0);
    return d;
  }, [selectedDay, time]);
  const endAt = startAt ? new Date(startAt.getTime() + duration * 60000) : null;

  const vars = useMemo(() => buildVars({
    contactName: context?.conversation.contactName || '', title, startAt: startAt || new Date(), responsavel: targetUser?.nome || '', mode, location, description
  }), [context, title, startAt, targetUser, mode, location, description]);

  const selectedTemplate = templates.find(t => t.name === templateName) || null;

  // Pré-preenche variáveis do template com dados da reunião quando o mapeamento dá a dica.
  const pickTemplate = (name: string) => {
    setTemplateName(name);
    const tpl = templates.find(t => t.name === name);
    if (!tpl) { setTemplateParams([]); return; }
    setTemplateParams(tpl.positions.map((position, index) => {
      const entry = tpl.variableOrder?.[index];
      if (typeof entry === 'string' && entry.startsWith(TEMPLATE_TEXT_PREFIX)) return entry.slice(TEMPLATE_TEXT_PREFIX.length);
      const key = String(entry || '').toLowerCase();
      const label = String(position.label || '').toLowerCase();
      if (key === 'nome' || label.includes('nome')) return vars.nome;
      if (key === 'primeironome') return vars.primeiroNome;
      if (key === 'telefone') return context?.conversation.contactPhone || '';
      if (label.includes('data')) return vars.data;
      if (label.includes('hora')) return vars.hora;
      return '';
    }));
  };

  const effectiveMinutes = customMinutes ? Math.max(1, Number(customMinutes) || 0) : minutesBefore;
  const reminderAt = startAt ? new Date(startAt.getTime() - effectiveMinutes * 60000) : null;
  const reminderTooLate = Boolean(reminderAt && reminderAt.getTime() <= Date.now() + 60000);

  const openDay = (day: Date) => {
    setSelectedDay(startOfDay(day));
    // horário sugerido: próximo slot livre a partir das 9h (ou da hora atual, se for hoje)
    const now = new Date();
    let h = 9;
    if (isSameDay(day, now)) h = Math.min(20, now.getHours() + 1);
    setTime(`${pad(h)}:00`);
  };

  const submit = async (ignoreConflicts = false) => {
    if (!targetUser || !startAt || !context) return;
    if (!title.trim()) { toast.error('Informe o título da reunião'); return; }
    if (startAt.getTime() <= Date.now()) { toast.error('A reunião precisa ser no futuro'); return; }
    const wantsNotify = notify && notificationMode !== 'none';
    if (wantsNotify && notificationMode === 'template') {
      if (!selectedTemplate) { toast.error('Selecione o template do lembrete'); return; }
      const missing = selectedTemplate.positions.findIndex((_p, i) => !String(templateParams[i] || '').trim());
      if (missing >= 0) { toast.error(`Preencha "${selectedTemplate.positions[missing].label}" — a Meta recusa template com variável vazia.`); return; }
    }
    setSaving(true);
    setConflicts(null);
    try {
      const data = await meetingFetch(`/api/meetings`, {
        method: 'POST',
        body: JSON.stringify({
          userId: targetUser.id, conversationId, title: title.trim(), startAt: startAt.toISOString(), endAt: endAt?.toISOString(),
          durationMinutes: duration, meetingMode: mode, location: location.trim() || null, description: description.trim() || null,
          ignoreConflicts,
          notify: wantsNotify ? {
            enabled: true, minutesBefore: effectiveMinutes,
            ...(notificationMode === 'text' ? { message } : {}),
            ...(notificationMode === 'template' && selectedTemplate ? { template: { name: selectedTemplate.name, language: selectedTemplate.language, params: templateParams.map(v => v.trim()), headerMedia: templateHeaderMedia.trim() || null } } : {})
          } : { enabled: false }
        })
      });
      setResult({ event: data.event, reminder: data.reminder, reminderWarning: data.reminderWarning || null });
      setStep('done');
      onScheduled?.(data.event);
      toast.success('Reunião agendada!');
    } catch (err: any) {
      if (err?.code === 'MEETING_CONFLICT' && Array.isArray(err.details?.conflicts)) {
        setConflicts(err.details.conflicts);
      } else {
        toast.error(err instanceof Error ? err.message : 'Erro ao agendar reunião', { duration: 7000 });
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelExisting = async (meeting: ExistingMeeting) => {
    if (!confirm(`Cancelar a reunião "${meeting.title}"? O lembrete ao contato (se houver) também será cancelado.`)) return;
    setCancellingId(meeting.id);
    try {
      await meetingFetch(`/api/meetings/${meeting.id}`, { method: 'PATCH', body: JSON.stringify({ status: 'CANCELLED' }) });
      setContext(c => c ? { ...c, meetings: c.meetings.map(m => m.id === meeting.id ? { ...m, status: 'CANCELLED' } : m) } : c);
      toast.success('Reunião cancelada');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao cancelar');
    } finally {
      setCancellingId(null);
    }
  };

  const stepIndex = { user: 0, calendar: 1, form: 2, done: 3 }[step];
  const contactName = context?.conversation.contactName || 'contato';
  const upcomingMeetings = (context?.meetings || []).filter(m => m.status !== 'CANCELLED' && new Date(m.startAt).getTime() >= Date.now() - 3600000);

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/50 p-3 sm:p-6" onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="flex max-h-[94vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Agendar reunião</h2>
            <p className="text-sm text-slate-500">
              com <span className="font-semibold text-slate-700">{contactName}</span>
              {targetUser && step !== 'user' && <> · na agenda de <span className="font-semibold text-slate-700">{targetUser.isMe ? 'você' : targetUser.nome}</span></>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(['Responsável', 'Data', 'Detalhes'] as const).map((label, i) => (
              <span key={label} className={`hidden items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold sm:flex ${i === stepIndex ? 'bg-cyan-600 text-white' : i < stepIndex ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                {i < stepIndex ? '✓' : i + 1} {label}
              </span>
            ))}
            <button onClick={onClose} className="ml-1 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Fechar">✕</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading || !context ? (
            <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600" /></div>
          ) : step === 'user' ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Para qual usuário do sistema a reunião será agendada? Ela ficará cravada na agenda dessa pessoa no Flow.</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {users.map(u => (
                  <button key={u.id} onClick={() => { setTargetUser(u); setStep('calendar'); }}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-left hover:border-cyan-400 hover:bg-cyan-50/40">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-sm font-bold text-cyan-700">{(u.nome || '?').slice(0, 1).toUpperCase()}</span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-slate-800">{u.nome}{u.isMe && <span className="ml-1 text-xs font-normal text-cyan-700">(você)</span>}</span>
                      <span className="block truncate text-xs text-slate-500">{u.email}</span>
                    </span>
                  </button>
                ))}
              </div>
              {upcomingMeetings.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-amber-800">Reuniões já marcadas com este contato</p>
                  <ul className="space-y-1.5">
                    {upcomingMeetings.map(m => (
                      <li key={m.id} className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${MEETING_STATUS_COLORS[m.status] || 'bg-slate-100'}`}>{MEETING_STATUS_LABELS[m.status] || m.status}</span>
                        <span className="font-medium">{m.title}</span>
                        <span className="text-slate-500">{new Date(m.startAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        {m.user && <span className="text-xs text-slate-500">· {m.user.nome}</span>}
                        <button onClick={() => cancelExisting(m)} disabled={cancellingId === m.id} className="ml-auto text-xs text-red-600 hover:underline disabled:opacity-50">Cancelar</button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : step === 'calendar' && targetUser ? (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
              <div>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setBase(new Date(base.getFullYear(), base.getMonth() - 1, 1))} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50">‹</button>
                    <button onClick={() => setBase(new Date())} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Hoje</button>
                    <button onClick={() => setBase(new Date(base.getFullYear(), base.getMonth() + 1, 1))} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50">›</button>
                  </div>
                  <span className="text-sm font-bold capitalize text-slate-800">{base.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</span>
                  {agendaLoading && <span className="text-xs text-slate-400">carregando…</span>}
                  {users.length > 1 && (
                    <button onClick={() => setStep('user')} className="ml-auto text-xs font-medium text-cyan-700 hover:underline">Trocar responsável</button>
                  )}
                </div>
                <MonthCalendar base={base} itemsByDay={itemsByDay} selectedDay={selectedDay} onDay={openDay} />
                <p className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
                  <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-amber-300" />Reunião</span>
                  <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-violet-300" />Compromisso</span>
                  <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-slate-300" />Tarefa com prazo</span>
                </p>
              </div>

              {/* Painel do dia */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                {!selectedDay ? (
                  <p className="text-sm text-slate-500">Clique em um dia da agenda de <b>{targetUser.isMe ? 'você' : targetUser.nome}</b> para escolher o horário.</p>
                ) : (
                  <>
                    <p className="text-sm font-bold capitalize text-slate-800">{selectedDay.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}</p>
                    <div className="mt-3">
                      <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Já na agenda</p>
                      {(itemsByDay[dayKey(selectedDay)] || []).length === 0 ? (
                        <p className="text-xs text-slate-400">Dia livre.</p>
                      ) : (
                        <ul className="max-h-36 space-y-1 overflow-y-auto">
                          {(itemsByDay[dayKey(selectedDay)] || []).map(item => (
                            <li key={`${item.kind}-${item.id}`} className={`flex items-center gap-2 rounded px-2 py-1 text-xs ${chipClass(item)}`}>
                              <span className="shrink-0 tabular-nums font-semibold">{hasTime(new Date(item.at)) ? timeStr(new Date(item.at)) : 'dia'}</span>
                              <span className="truncate">{item.title}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <label className="text-xs font-semibold text-slate-600">Horário
                        <input type="time" value={time} onChange={e => setTime(e.target.value)} step={300} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                      </label>
                      <label className="text-xs font-semibold text-slate-600">Duração
                        <select value={duration} onChange={e => setDuration(Number(e.target.value))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                          {DURATIONS.map(d => <option key={d} value={d}>{d >= 60 ? `${d / 60}h${d % 60 ? ` ${d % 60}min` : ''}` : `${d} min`}</option>)}
                        </select>
                      </label>
                    </div>
                    <SlotPicker day={selectedDay} items={itemsByDay[dayKey(selectedDay)] || []} duration={duration} value={time} onPick={setTime} />
                    {startAt && startAt.getTime() <= Date.now() && <p className="mt-2 text-xs text-red-600">Esse horário já passou.</p>}
                    <button onClick={() => setStep('form')} disabled={!startAt || startAt.getTime() <= Date.now()}
                      className="mt-3 w-full rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50">
                      Continuar → {startAt ? `${dateStr(startAt)} ${timeStr(startAt)}` : ''}
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : step === 'form' && targetUser && startAt ? (
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {/* Detalhes */}
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-xl border border-cyan-200 bg-cyan-50/60 px-4 py-2.5 text-sm">
                  <span className="capitalize text-slate-700"><b>{weekdayStr(startAt)}, {dateStr(startAt)}</b> · {timeStr(startAt)}–{endAt ? timeStr(endAt) : ''} ({duration} min)</span>
                  <button onClick={() => setStep('calendar')} className="text-xs font-medium text-cyan-700 hover:underline">Alterar</button>
                </div>
                <label className="block text-xs font-semibold text-slate-600">Título *
                  <input value={title} onChange={e => setTitle(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Ex.: Apresentação da proposta" />
                </label>
                <div>
                  <p className="text-xs font-semibold text-slate-600">Como será</p>
                  <div className="mt-1 flex gap-2">
                    {MODES.map(m => (
                      <button key={m.value} type="button" onClick={() => setMode(m.value)}
                        className={`flex-1 rounded-lg border px-3 py-1.5 text-xs font-semibold ${mode === m.value ? 'border-cyan-600 bg-cyan-600 text-white' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{m.label}</button>
                    ))}
                  </div>
                </div>
                <label className="block text-xs font-semibold text-slate-600">{mode === 'ONLINE' ? 'Link da reunião' : mode === 'TELEFONE' ? 'Telefone' : 'Endereço'}
                  <input value={location} onChange={e => setLocation(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder={MODES.find(m => m.value === mode)?.hint} />
                </label>
                <label className="block text-xs font-semibold text-slate-600">Descrição / pauta
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="O que será tratado, materiais a levar, contexto do contato..." />
                </label>
                <p className="text-xs text-slate-500">Responsável: <b>{targetUser.nome}</b>{!targetUser.isMe && ' (será notificado no Flow)'} · Contato: <b>{contactName}</b>{context.conversation.contactPhone ? ` · ${context.conversation.contactPhone}` : ''}</p>
              </div>

              {/* Lembrete */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <p className="text-sm font-bold text-slate-800">Lembrete ao contato</p>
                {notificationMode === 'none' ? (
                  <p className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">{context.notification.reason}</p>
                ) : (
                  <>
                    <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} className="h-4 w-4 accent-cyan-600" />
                      Notificar <b>{contactName}</b> sobre a reunião
                    </label>
                    <p className="mt-1 text-[11px] text-slate-500">{context.notification.reason}</p>
                    {notify && (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <label className="text-xs font-semibold text-slate-600">Quanto tempo antes
                            <select value={customMinutes ? 'custom' : minutesBefore} onChange={e => { if (e.target.value === 'custom') setCustomMinutes('60'); else { setCustomMinutes(''); setMinutesBefore(Number(e.target.value)); } }}
                              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
                              {context.reminderOffsets.map(o => <option key={o.minutes} value={o.minutes}>{o.label}</option>)}
                              <option value="custom">Personalizado…</option>
                            </select>
                          </label>
                          {customMinutes !== '' && (
                            <label className="text-xs font-semibold text-slate-600">Minutos antes
                              <input type="number" min={1} value={customMinutes} onChange={e => setCustomMinutes(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                            </label>
                          )}
                        </div>
                        <p className={`text-xs ${reminderTooLate ? 'text-red-600' : 'text-slate-500'}`}>
                          {reminderAt ? (reminderTooLate ? 'Esse horário de lembrete já passou — a reunião será criada sem lembrete.' : `Envio previsto: ${dateStr(reminderAt)} às ${timeStr(reminderAt)}`) : ''}
                        </p>

                        {notificationMode === 'text' ? (
                          <>
                            <label className="block text-xs font-semibold text-slate-600">Mensagem
                              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={6} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                            </label>
                            <div className="flex flex-wrap gap-1">
                              {context.reminderVariables.map(v => (
                                <button key={v.key} type="button" title={v.label} onClick={() => setMessage(m => `${m}${m && !m.endsWith(' ') ? ' ' : ''}{{${v.key}}}`)}
                                  className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:border-cyan-400 hover:text-cyan-700">{`{{${v.key}}}`}</button>
                              ))}
                              <button type="button" onClick={() => setMessage(context.defaults.reminderMessage)} className="rounded-full px-2 py-0.5 text-[10px] font-medium text-cyan-700 hover:underline">restaurar padrão</button>
                            </div>
                            <div>
                              <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Prévia</p>
                              <div className="whitespace-pre-wrap rounded-xl bg-[#dcf8c6] px-3 py-2 text-sm text-slate-800 shadow-sm">{renderVars(message, vars) || <span className="text-slate-400">mensagem vazia</span>}</div>
                            </div>
                          </>
                        ) : (
                          <>
                            {templatesLoading ? <p className="text-xs text-slate-500">Carregando templates aprovados…</p>
                              : templatesError ? <p className="text-xs text-red-600">{templatesError}</p>
                              : templates.length === 0 ? <p className="text-xs text-amber-700">Nenhum template aprovado nesta conta. Crie e aprove um template de lembrete em Campanhas &gt; Templates para poder notificar.</p>
                              : (
                                <label className="block text-xs font-semibold text-slate-600">Template aprovado
                                  <select value={templateName} onChange={e => pickTemplate(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm">
                                    <option value="">Selecione…</option>
                                    {templates.map(t => <option key={`${t.name}-${t.language}`} value={t.name}>{t.name} · {t.language}{t.category ? ` · ${t.category}` : ''}</option>)}
                                  </select>
                                </label>
                              )}
                            {selectedTemplate && (
                              <div className="space-y-2">
                                {selectedTemplate.positions.map((position, index) => (
                                  <label key={position.key} className="block text-xs font-semibold text-slate-600">{position.label}
                                    <div className="mt-1 flex gap-1">
                                      <input value={templateParams[index] || ''} onChange={e => setTemplateParams(p => { const n = [...p]; n[index] = e.target.value; return n; })} className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                                      <select value="" onChange={e => { const v = vars[e.target.value]; if (v !== undefined) setTemplateParams(p => { const n = [...p]; n[index] = v; return n; }); }} className="rounded-lg border border-slate-300 bg-white px-1 text-[11px] text-slate-600" title="Preencher com dado da reunião">
                                        <option value="">usar…</option>
                                        {context.reminderVariables.map(v => <option key={v.key} value={v.key}>{v.label}</option>)}
                                      </select>
                                    </div>
                                  </label>
                                ))}
                                {selectedTemplate.headerFormat && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTemplate.headerFormat) && (
                                  <label className="block text-xs font-semibold text-slate-600">URL da mídia do cabeçalho ({selectedTemplate.headerFormat.toLowerCase()})
                                    <input value={templateHeaderMedia} onChange={e => setTemplateHeaderMedia(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="https://..." />
                                  </label>
                                )}
                                <div>
                                  <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Prévia</p>
                                  <div className="whitespace-pre-wrap rounded-xl bg-[#dcf8c6] px-3 py-2 text-sm text-slate-800 shadow-sm">{renderTemplatePreview(selectedTemplate, templateParams)}</div>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {conflicts && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 lg:col-span-2">
                  <p className="text-sm font-bold text-amber-900">Choque de horário na agenda de {targetUser.nome}</p>
                  <ul className="mt-1 list-disc pl-5 text-xs text-amber-900">
                    {conflicts.map(c => <li key={c.id}>{c.title} — {new Date(c.startAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}{c.endAt ? ` até ${timeStr(new Date(c.endAt))}` : ''}</li>)}
                  </ul>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => { setConflicts(null); setStep('calendar'); }} className="rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100">Escolher outro horário</button>
                    <button onClick={() => submit(true)} disabled={saving} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50">Agendar mesmo assim</button>
                  </div>
                </div>
              )}
            </div>
          ) : step === 'done' && result ? (
            <div className="mx-auto max-w-lg space-y-4 py-4 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-2xl">📅</div>
              <h3 className="text-lg font-bold text-slate-900">Reunião agendada</h3>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-left text-sm text-slate-700">
                <p><b>{result.event.title}</b></p>
                <p className="capitalize">{weekdayStr(new Date(result.event.startAt))}, {dateStr(new Date(result.event.startAt))} · {timeStr(new Date(result.event.startAt))}{result.event.endAt ? `–${timeStr(new Date(result.event.endAt))}` : ''}</p>
                <p>Responsável: {result.event.user?.nome || targetUser?.nome} · {MODES.find(m => m.value === result.event.meetingMode)?.label || ''}{result.event.location ? ` · ${result.event.location}` : ''}</p>
                <p className="mt-2 text-xs text-slate-500">Cravada na agenda do Flow. {result.reminder ? `Lembrete ao contato ${offsetLabel(effectiveMinutes, context.reminderOffsets)} (${new Date(result.reminder.scheduledFor).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}).` : 'Sem lembrete ao contato.'}</p>
                {result.reminderWarning && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">{result.reminderWarning}</p>}
              </div>
              <button onClick={onClose} className="rounded-lg bg-cyan-600 px-5 py-2 text-sm font-semibold text-white hover:bg-cyan-700">Fechar</button>
            </div>
          ) : null}
        </div>

        {step === 'form' && !conflicts && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-5 py-3">
            <button onClick={() => setStep('calendar')} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">← Voltar</button>
            <button onClick={() => submit(false)} disabled={saving} className="rounded-lg bg-cyan-600 px-5 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-50">
              {saving ? 'Agendando…' : notify && notificationMode !== 'none' ? 'Agendar e programar lembrete' : 'Agendar reunião'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Grade mensal (mesmo desenho da agenda do Flow) para escolher o dia.
function MonthCalendar({ base, itemsByDay, selectedDay, onDay }: { base: Date; itemsByDay: Record<string, AgendaItem[]>; selectedDay: Date | null; onDay: (d: Date) => void }) {
  const cells = monthGrid(base);
  const today = startOfDay(new Date());
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <div className="min-w-[520px]">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
            {WEEKDAYS.map(w => <div key={w} className="py-2 text-center text-xs font-bold uppercase tracking-wider text-slate-500">{w}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((day, i) => {
              const dayItems = itemsByDay[dayKey(day)] || [];
              const inMonth = day.getMonth() === base.getMonth();
              const past = day.getTime() < today.getTime();
              const isSel = selectedDay ? isSameDay(day, selectedDay) : false;
              const isTodayCell = isSameDay(day, today);
              return (
                <button key={i} type="button" disabled={past} onClick={() => onDay(day)}
                  className={`min-h-[84px] border-b border-r border-slate-100 p-1.5 text-left transition ${past ? 'cursor-not-allowed bg-slate-50 opacity-50' : 'hover:bg-cyan-50/50'} ${isSel ? 'bg-cyan-50 ring-2 ring-inset ring-cyan-500' : inMonth ? 'bg-white' : 'bg-slate-50/60'}`}>
                  <div className="mb-1 flex justify-end">
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isTodayCell ? 'bg-cyan-600 text-white' : inMonth ? 'text-slate-700' : 'text-slate-400'}`}>{day.getDate()}</span>
                  </div>
                  <div className="space-y-0.5">
                    {dayItems.slice(0, 3).map(item => (
                      <div key={`${item.kind}-${item.id}`} className={`flex items-center gap-1 truncate rounded px-1 py-0.5 text-[10px] font-medium ${chipClass(item)}`} title={item.title}>
                        {hasTime(new Date(item.at)) && <span className="shrink-0 tabular-nums opacity-70">{timeStr(new Date(item.at))}</span>}
                        <span className="truncate">{item.title}</span>
                      </div>
                    ))}
                    {dayItems.length > 3 && <p className="pl-1 text-[10px] font-semibold text-slate-400">+{dayItems.length - 3} mais</p>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Horários do dia em blocos de 30 min; ocupado = choca com item que tem horário.
function SlotPicker({ day, items, duration, value, onPick }: { day: Date; items: AgendaItem[]; duration: number; value: string; onPick: (t: string) => void }) {
  const slots: { label: string; busy: boolean; past: boolean }[] = [];
  const busyRanges = items.filter(i => i.kind !== 'task' && i.status !== 'CANCELLED' && hasTime(new Date(i.at))).map(i => {
    const s = new Date(i.at).getTime();
    const e = i.endAt ? new Date(i.endAt).getTime() : s + 60 * 60000;
    return [s, e];
  });
  for (let h = 7; h <= 20; h++) {
    for (const m of [0, 30]) {
      const d = new Date(day); d.setHours(h, m, 0, 0);
      const s = d.getTime(); const e = s + duration * 60000;
      slots.push({ label: `${pad(h)}:${pad(m)}`, busy: busyRanges.some(([bs, be]) => s < be && e > bs), past: s <= Date.now() });
    }
  }
  return (
    <div className="mt-3">
      <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">Horários (7h–20h)</p>
      <div className="grid max-h-40 grid-cols-4 gap-1 overflow-y-auto pr-1">
        {slots.map(slot => (
          <button key={slot.label} type="button" disabled={slot.past} onClick={() => onPick(slot.label)}
            className={`rounded px-1 py-1 text-[11px] font-semibold tabular-nums ${value === slot.label ? 'bg-cyan-600 text-white' : slot.past ? 'bg-slate-100 text-slate-300' : slot.busy ? 'bg-rose-50 text-rose-500 line-through hover:bg-rose-100' : 'bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-cyan-50'}`}
            title={slot.busy ? 'Choca com outro compromisso' : ''}>{slot.label}</button>
        ))}
      </div>
    </div>
  );
}
